import { useEffect, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { usePageMeta } from "../usePageMeta";

/** What GET /api/pay/<id> answers (app/routers/admin.py, read_quote). */
interface Quote {
  item_name: string;
  amount_cents: number;
  currency: string;
  /** With its UTC offset, so it reads right in the customer's own zone. */
  expires_at: string;
  state: "open" | "expired" | "cancelled" | "paid";
  /** Whether /api/pay/<id>/preview has an image to show. */
  has_preview: boolean;
  stitch_preview_url: string | null;
}

interface PayPageProps {
  apiUrl: string;
  orderId: string;
}

const PAGE_TITLE = "Pay for your design | Embroidery Text";
const PAGE_DESCRIPTION = "Pay for your custom digitized embroidery design.";
const SUPPORT_ADDRESS = "support@embroiderytext.com";

function price(quote: Quote): string {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: quote.currency.toUpperCase(),
  }).format(quote.amount_cents / 100);
}

function longDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

/**
 * Where a custom order's payment link lands: /pay/<order id>.
 *
 * The emailed link is this page, not Stripe's checkout, because a Stripe
 * session lasts a day at most and a quote lasts longer. Opening the page
 * only reads the quote; the Stripe session is made when Pay is pressed, so
 * a mail scanner following the link creates nothing. Stripe sends the
 * customer back here: with ?paid=1 once they've paid, and without it if
 * they backed out, which shows the Pay button again.
 *
 * Analytics: quote_viewed once per load, with the state it showed
 * (open, paid, expired, cancelled, or missing), then quote_pay_clicked,
 * quote_checkout_unavailable if that was refused, and quote_paid_returned on
 * the way back from Stripe. Whether they looked at the design:
 * quote_preview_shown once the image has actually loaded (or
 * quote_preview_failed), quote_preview_opened when they open it full size,
 * and quote_stitch_preview_clicked for the stitch preview link. Each carries order_id, as do the server's
 * order_* events (app/routers/admin.py, _track), which are the ones to count
 * payments by: this page can be blocked or closed, the webhook can't.
 */
export function PayPage({ apiUrl, orderId }: PayPageProps) {
  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: `/pay/${orderId}` });
  const justPaid = new URLSearchParams(window.location.search).has("paid");
  const [quote, setQuote] = useState<Quote | null | "missing">(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const posthog = usePostHog();
  // StrictMode runs effects twice in development; one view is one view.
  const viewReported = useRef(false);
  const previewReported = useRef(false);
  const previewUrl = `${apiUrl}/api/pay/${orderId}/preview`;

  const track = (event: string, properties: Record<string, unknown> = {}) =>
    posthog?.capture(event, { order_id: orderId, ...properties });

  const report = (shown: Quote | "missing") => {
    if (viewReported.current) return;
    viewReported.current = true;
    if (shown === "missing") {
      track("quote_viewed", { state: "missing" });
      return;
    }
    const props = {
      state: shown.state,
      amount_cents: shown.amount_cents,
      currency: shown.currency,
      days_left: Math.round((new Date(shown.expires_at).getTime() - Date.now()) / 86400000),
      has_preview: shown.has_preview,
      has_stitch_preview: Boolean(shown.stitch_preview_url),
    };
    track("quote_viewed", props);
    if (justPaid) track("quote_paid_returned", props);
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/pay/${orderId}`);
        if (res.status === 404) {
          setQuote("missing");
          report("missing");
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const loaded: Quote = await res.json();
        setQuote(loaded);
        report(loaded);
      } catch {
        setError("We couldn't load this quote. Try again in a moment.");
      }
    })();
  }, [orderId]);

  const pay = async () => {
    setBusy(true);
    setError(null);
    track("quote_pay_clicked", typeof quote === "object" && quote ? { amount_cents: quote.amount_cents } : {});
    try {
      const res = await fetch(`${apiUrl}/api/pay/${orderId}/checkout`, { method: "POST" });
      if (res.ok) {
        const { url } = await res.json();
        window.location.assign(url);
        return; // Leave busy set: the page is navigating away.
      }
      if (res.status === 409) {
        // The quote changed state since the page loaded (expired, cancelled,
        // or paid in another tab): reload it so the page says which.
        const latest = await fetch(`${apiUrl}/api/pay/${orderId}`);
        const now: Quote | null = latest.ok ? await latest.json() : null;
        if (now) setQuote(now);
        track("quote_checkout_unavailable", { reason: now?.state ?? "conflict", http_status: 409 });
      } else {
        track("quote_checkout_unavailable", { reason: "error", http_status: res.status });
        setError("Payment is unavailable right now. Try again in a moment.");
      }
    } catch {
      track("quote_checkout_unavailable", { reason: "network" });
      setError("Payment is unavailable right now. Try again in a moment.");
    }
    setBusy(false);
  };

  const contact = (
    <a href={`mailto:${SUPPORT_ADDRESS}`}>{SUPPORT_ADDRESS}</a>
  );

  if (quote === null) {
    return error ? (
      <article><p>{error}</p></article>
    ) : (
      <p aria-busy="true">Loading…</p>
    );
  }

  if (quote === "missing") {
    return (
      <article>
        <h2>Quote not found</h2>
        <p>This payment link isn't valid. Check it against your email, or write to {contact}.</p>
      </article>
    );
  }

  // The webhook that marks the order paid can land a moment after Stripe
  // sends the customer back, so ?paid=1 is trusted for the thank-you.
  if (justPaid || quote.state === "paid") {
    return (
      <article>
        <h2>Thank you</h2>
        <p>
          Your payment for {quote.item_name} went through. We'll email your
          design files shortly.
        </p>
        <p><small>Questions? Write to {contact}.</small></p>
      </article>
    );
  }

  if (quote.state === "cancelled" || quote.state === "expired") {
    return (
      <article>
        <h2>{quote.state === "cancelled" ? "This quote was cancelled" : "This quote has expired"}</h2>
        <p>
          It can no longer be paid. If you'd still like the design, write to{" "}
          {contact} and we'll send you a new link.
        </p>
      </article>
    );
  }

  return (
    <article>
      <h2>{quote.item_name}</h2>
      <p style={{ fontSize: "2rem", margin: "0 0 0.5rem" }}>{price(quote)}</p>
      <p>
        <small>This quote can be paid until {longDate(quote.expires_at)}.</small>
      </p>
      {quote.has_preview && (
        <figure style={{ margin: "0 0 1rem" }}>
          {/* Opens full size in a new tab; the click is the signal that they
              looked closely, not just scrolled past. */}
          <a
            href={previewUrl}
            target="_blank"
            rel="noopener"
            onClick={() => track("quote_preview_opened")}
          >
            <img
              src={previewUrl}
              alt={`Preview of your ${quote.item_name} design`}
              style={{ display: "block", maxWidth: "100%", maxHeight: "28rem", borderRadius: "var(--pico-border-radius)" }}
              onLoad={() => {
                if (previewReported.current) return;
                previewReported.current = true;
                track("quote_preview_shown");
              }}
              onError={() => {
                if (previewReported.current) return;
                previewReported.current = true;
                track("quote_preview_failed");
              }}
            />
          </a>
          <figcaption><small>Tap the image to see it full size.</small></figcaption>
        </figure>
      )}
      {quote.stitch_preview_url && (
        <p>
          <a
            href={quote.stitch_preview_url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("quote_stitch_preview_clicked")}
          >
            Watch it stitch
          </a>
        </p>
      )}
      <button type="button" onClick={pay} disabled={busy} aria-busy={busy}>
        Pay {price(quote)}
      </button>
      {error && <p style={{ color: "var(--pico-del-color, #b3261e)" }}>{error}</p>}
      <p>
        <small>
          You'll pay securely through Stripe. Questions? Write to {contact}.
        </small>
      </p>
    </article>
  );
}
